import React, { useState, useRef } from "react"
import { makeStyles } from "@material-ui/styles"
import {
  DialogActions,
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  IconButton,
  Divider,
  Box,
  Hidden,
} from "@material-ui/core"
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown"
import AddPhotoAlternateOutlinedIcon from "@material-ui/icons/AddPhotoAlternateOutlined"
import AlternateEmailOutlinedIcon from "@material-ui/icons/AlternateEmailOutlined"
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined"
import ScheduleIcon from "@material-ui/icons/Schedule"
import Config from "react-global-configuration"
import userflow from "userflow.js"
import moment from "moment"
import { useParams } from "react-router-dom"
import { useMutationCreatePost, useMutationUpdatePost, makeCreatePostOptimisticResponse } from "../../data"
import {
  MentionTextInput,
  LinkButton,
  DialogDateTimePicker,
  Blockquote,
  UsersGroupsAssigner,
  DraftBlockquote,
  Uploader,
  UploadingList,
  UploadingImagesList,
} from ".."
import { usePostUtils, toId, mapToIds, useMountEffect, useFormUtils, useDraft } from "../../utils"

import { CreatorMaster } from "../Creators"

import { RowBox } from "../Boxes"
import { useAuth } from "../../services"
import { LocationsAssigner, usersGroupsEveryoneItem } from "../Assigners"
import { LoadingSpinner } from "../LoadingSpinner"

const useStyles = makeStyles((theme) => ({
  settings: {
    minHeight: 68,
  },
  scheduledText: {
    color: theme.palette.scheduled.main,
  },
  dividerSpacer: {
    marginBottom: theme.spacing(2),
  },
  actions: {
    paddingTop: theme.spacing(1),
    fontSize: 14,
  },
  buttonText: {
    fontWeight: "600",
    fontSize: 14,
  },
  postOptions: {
    padding: theme.spacing(3),
  },
  visibleFrom: {},
}))

const initialState = {
  title: "",
  content: null,
  job: null,
  knowledge: null,
  hasComments: true,
  hasConfirm: false,
  showPostOptions: false,
  showPostOptionsEl: null,
  locations: [],
  users: [],
  groups: [],
  uploads: [],
}

const PostCreator = ({ open, onClose, edit, isInline }) => {
  const classes = useStyles()
  const { set } = useParams()
  const [createPost, { loading: createLoading }] = useMutationCreatePost({ set })
  const [updatePost, { loading: updateLoading }] = useMutationUpdatePost()
  const { isValid, touched } = useFormUtils()
  const { getScheduleFormattedDate, isDateInFuture } = usePostUtils()
  const {
    support: {
      userflow: { enabled: userflowEnabled },
    },
  } = Config.get()
  const {
    settings: { locations: userLocations },
    principal,
  } = useAuth()

  const [title, setTitle] = useState(initialState.title)
  const [content, setContent] = useState(initialState.content)
  const [job, setJob] = useState(initialState.job)
  const [knowledge, setKnowledge] = useState(initialState.knowledge)
  const [hasComments, setHasComments] = useState(initialState.hasComments)
  const [hasConfirm, setHasConfirm] = useState(initialState.hasConfirm)
  const [showPostOptions, setShowPostOptions] = useState(initialState.showPostOptions)
  const [showPostOptionsEl, setShowPostOptionsEl] = useState(initialState.showPostOptionsEl)
  const [showScheduler, setShowScheduler] = useState(false)
  const [hasEveryone, setHasEveryone] = useState(false)
  const [locations, setLocations] = useState(initialState.locations)
  const [users, setUsers] = useState(initialState.users)
  const [groups, setGroups] = useState(initialState.groups)
  const [uploads, setUploads] = useState(initialState.uploads)
  const [uploading, setUploading] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const contentRef = useRef(null)
  const [visibleFrom, setVisibleFrom] = useState(null)

  const [locationNames, setLocationNames] = useState([])
  const [initialPicked, setInitialPicked] = useState(null)

  const [draft, setDraft, removeDraft, draftUntouched] = useDraft("post_creator", null)

  const isEdit = edit != null
  const scheduledInFuture = isDateInFuture(visibleFrom)

  useMountEffect(() => {
    if (edit) {
      const editLocationIds = mapToIds(edit.locations)

      const noUsersOrGroups = !edit.users.length && !edit.groups.length

      setTitle(edit.title)
      setContent(edit.content)
      setJob(toId(edit.job, true))
      setKnowledge(toId(edit.knowledge, true))
      setHasComments(edit.hasComments)
      setHasConfirm(edit.hasConfirm)
      setLocations([
        ...edit.locations.map((location) => {
          const item = userLocations.find((ul) => toId(ul) === toId(location))
          if (item) return item
          return { id: toId(location), name: "(Hidden)", noAccess: true }
        }),
      ])
      setUsers(edit.users.map((user) => toId(user)))
      setGroups(edit.groups.map((group) => toId(group)))
      setUploads(edit.uploads)
      setHasEveryone(!edit.users.length && !edit.groups.length)
      setVisibleFrom(edit.visibleFrom)

      setLocationNames(
        userLocations.filter((userLocation) => editLocationIds.includes(toId(userLocation))).map(({ name }) => name)
      )
      setInitialPicked(noUsersOrGroups ? [usersGroupsEveryoneItem] : [...edit.users, ...edit.groups])
    } else {
      setLocations(!userLocations.length || userLocations.length > 1 ? [] : [userLocations[0]])
      setLocationNames(!userLocations.length || userLocations.length > 1 ? [] : [userLocations[0].name])
      setInitialPicked([])

      if (draft) {
        draft.title && setTitle(draft.title)
        draft.content && setContent(draft.content)
        draft.uploads && setUploads(draft.uploads)
        draft.visibleFrom && setVisibleFrom(draft.visibleFrom)
      }
    }
  }, [edit, userLocations])

  const handleToggleCreateMenu = ({ currentTarget }) => {
    setShowPostOptionsEl(!showPostOptions ? currentTarget : null)
    setShowPostOptions(!showPostOptions)
  }

  const handleToggleScheduler = () => {
    setShowScheduler(!showScheduler)
    setShowPostOptions(false)
  }

  const handleSchedulerPickedDate = (date) => {
    setVisibleFrom(date.format())
    setShowScheduler(false)
  }

  const handleClose = () => {
    onClose(true)
  }

  const handlePostNow = (e) => {
    handleSubmit(e, moment().add(-1, "seconds").format())
  }
  const handlePostLater = (e) => {
    handleSubmit(e, visibleFrom)
  }

  const handleSubmit = async (e, visibleFromValue) => {
    e.preventDefault()
    if (formValid()) {
      const usersGroups = { users: hasEveryone ? [] : users, groups: hasEveryone ? [] : groups }
      const uploadIds = uploads.map((upload) => upload.id)
      const variables = {
        title,
        content,
        job,
        knowledge,
        hasComments,
        hasConfirm,
        visibleFrom: visibleFromValue,
        locations: locations.map((location) => toId(location)),
        ...usersGroups,
        uploads: uploadIds,
      }
      if (isEdit) {
        await updatePost({
          variables: {
            id: edit.id,
            ...variables,
          },
        })
      } else {
        const optimisticResponse = makeCreatePostOptimisticResponse({
          post: variables,
          principal,
        })
        createPost({
          variables,
          optimisticResponse,
        })
        removeDraft()
        if (userflowEnabled) userflow.track("created_post")
      }
      handleClose()
    }
  }

  const handleChange = (setter, name, newValue) => {
    setter(newValue)
    setDraftProperty(name, newValue)
  }

  const setDraftProperty = (prop, value) => {
    if (edit) return
    setDraft((prev) => ({
      ...prev,
      [prop]: value,
    }))
  }

  const handleDiscardDraft = () => {
    removeDraft()
    setTitle(initialState.title)
    setContent(initialState.content)
    setUploads(initialState.uploads)
    setVisibleFrom(null)
  }

  const handleLocationsPickedChanged = (picked) => {
    const pickedLocationIds = mapToIds(picked)

    setLocations([...pickedLocationIds])

    setLocationNames(
      userLocations.filter((userLocation) => pickedLocationIds.includes(toId(userLocation))).map(({ name }) => name)
    )
  }

  const handleUsersGroupsPickedChanged = (picked) => {
    const newHasEveryone = picked.find((p) => p.__typename === "Everyone")
    setHasEveryone(newHasEveryone)
    setUsers(newHasEveryone ? [] : [...mapToIds(picked.filter((p) => p.__typename === "User"))])
    setGroups(newHasEveryone ? [] : [...mapToIds(picked.filter((p) => p.__typename === "Group"))])
  }

  const handleAddMention = (id) => {
    const parts = id.split(":")
    if (parts.length >= 2) {
      const newId = parts[1]
      switch (parts[0]) {
        case "User":
          if (!users.includes(newId)) setUsers([...users, newId])
          break
        case "Group":
          if (!groups.includes(newId)) setGroups([...groups, newId])
          break
        case "MentionableTask":
        case "MentionableProcessJob":
          setJob(newId)
          break
        case "Knowledge":
          setKnowledge(newId)
          break
        default:
      }
    }
  }

  const handleAtClick = () => {
    setContent(content ? `${content.trimEnd()} @` : "@")
    contentRef.current.focus()
  }

  const handleBeforeUpload = (items) => {
    setIsUploading(true)
    setUploading(() => items)
  }
  const handleUploadProgress = (items) => {
    setUploading(() => items)
  }
  const handleItemUploaded = (item) => {
    setUploads((state) => {
      const result = [...state, item]
      setDraftProperty("uploads", [...result])
      return result
    })
  }
  const handleRemoveUpload = (id) => {
    setUploads((state) => {
      const result = [...state.filter((upload) => upload.id !== id)]
      setDraftProperty("uploads", [...result])
      return result
    })
  }
  const handleNotUploading = () => {
    setIsUploading(false)
  }

  const formValid = () => isValid(title, content) && (hasEveryone || users.length || groups.length) && !isUploading

  const isError = (val) => (val != null ? val.length === 0 : false)
  const errorMessage = (val, message) => isError(val) && message

  const submitLoading = createLoading || updateLoading

  const assignToDisabled = !locations || !locations.length

  const isFormValid = formValid()

  const showDraft = !edit && !!draft && draftUntouched

  const form = (
    <>
      <DraftBlockquote show={showDraft} subject="Post" onDiscard={handleDiscardDraft} />

      {locations && userLocations.length > 1 && (
        <LocationsAssigner
          initialPicked={locations}
          onPickedChanged={handleLocationsPickedChanged}
          requireTimeZoneMatch={false}
          cy="PostCreator-locations"
        />
      )}
      {initialPicked && (
        <UsersGroupsAssigner
          title="Assign to"
          placeholder={assignToDisabled ? "No locations selected" : "Nobody selected"}
          allStaffLabel={`All staff (${locationNames.join(", ")})`}
          locations={locations.map((location) => toId(location))}
          topBorder={userLocations.length <= 1}
          initialPicked={initialPicked}
          onPickedChanged={handleUsersGroupsPickedChanged}
          disabled={assignToDisabled}
          cy="PostCreator-to"
        />
      )}
      <Box mb={1}>
        <TextField
          variant="outlined"
          margin="normal"
          fullWidth
          id="title"
          label="Post title"
          name="title"
          value={title}
          required
          onChange={(event) => handleChange(setTitle, "title", event.target.value)}
          inputProps={{ "data-cy": "PostCreator-title" }}
        />
      </Box>
      <MentionTextInput
        inputRef={contentRef}
        label="Post content"
        value={content}
        locations={locations}
        height={190}
        onChange={(event) => handleChange(setContent, "content", event.target.value)}
        onAddMention={handleAddMention}
        fullWidth
        multiline
        required
        onBlur={() => touched(content, setContent)}
        error={isError(content)}
        helperText={errorMessage(content, "Please enter content")}
        cy="PostCreator-content"
      />
      {uploads && uploads.length > 0 && (
        <Box my={1}>
          <UploadingImagesList
            uploaded={uploads}
            uploading={uploading}
            onRemoveUpload={handleRemoveUpload}
            height={100}
            gridItemProps={{
              xs: 3,
            }}
          />
          <UploadingList uploaded={uploads} uploading={uploading} images={false} onRemoveUpload={handleRemoveUpload} />
        </Box>
      )}
      <Grid container direction="row" justifyContent="space-between" alignItems="center" className={classes.settings}>
        <Grid item sm={3} xs={12}>
          <IconButton onClick={handleAtClick} title="Link this jobs, articles or people to this post">
            <AlternateEmailOutlinedIcon />
          </IconButton>
          <Uploader
            onBeforeUpload={handleBeforeUpload}
            onProgress={handleUploadProgress}
            onItemUploaded={handleItemUploaded}
            onAfterUpload={handleNotUploading}
            onError={handleNotUploading}
            images={false}
            disabled={isUploading}
            title="Attach files to this post"
          />
          <Uploader
            onBeforeUpload={handleBeforeUpload}
            onProgress={handleUploadProgress}
            onItemUploaded={handleItemUploaded}
            onAfterUpload={handleNotUploading}
            onError={handleNotUploading}
            icon={<AddPhotoAlternateOutlinedIcon />}
            documents={false}
            disabled={isUploading}
            title="Attach photos / images to this post"
          />
        </Grid>
        <Grid item sm={9} xs={12} container direction="row" justify-sm="flex-end" justify-xs="flex-start">
          <FormControlLabel
            control={
              <Switch
                checked={hasComments}
                onChange={() => setHasComments(!hasComments)}
                value={hasComments}
                color="primary"
              />
            }
            label="Allow comments"
          />
          <FormControlLabel
            control={
              <Switch
                checked={hasConfirm}
                onChange={() => setHasConfirm(!hasConfirm)}
                value={hasConfirm}
                color="primary"
              />
            }
            label="Require confirmation from readers"
          />
        </Grid>
      </Grid>
      <Divider className={classes.dividerSpacer} />
      {hasConfirm && (
        <Box mb={1}>
          <Blockquote
            icon={<InfoOutlinedIcon />}
            title="This post will require confirmation"
            body="All users that this post is shared with will be required to mark that they have seen and read the post"
          />
        </Box>
      )}

      <Hidden smUp>
        <Grid container direction="row" alignItems="center" spacing={3}>
          <Grid item xs={12}>
            {/* TODO make mobile friendly version */}
            {scheduledInFuture && (
              <RowBox mr="auto" className={classes.scheduledText}>
                <ScheduleIcon />
                <Box ml={1}>
                  Post scheduled for <strong>{getScheduleFormattedDate(visibleFrom)}</strong>
                </Box>
              </RowBox>
            )}
          </Grid>
          <Grid item xs={12}>
            <RowBox>
              <Box mr={2}>
                <LinkButton
                  onClick={handleClose}
                  className={`${classes.button} ${classes.buttonText}`}
                  disabled={submitLoading}
                >
                  Cancel
                </LinkButton>
              </Box>
              <Box ml="auto">
                <ButtonGroup
                  variant="contained"
                  color="primary"
                  aria-label={scheduledInFuture ? "Post as scheduled" : "Post now"}
                  className={classes.button}
                >
                  <Button
                    onClick={scheduledInFuture ? handlePostLater : handlePostNow}
                    className={classes.buttonText}
                    disabled={!isFormValid || submitLoading}
                  >
                    {!submitLoading && <>{scheduledInFuture ? "Post as scheduled" : "Post now"}</>}
                    {submitLoading && <LoadingSpinner size="24px" delay={false} />}
                  </Button>
                  <Button
                    size="small"
                    onClick={handleToggleCreateMenu}
                    className={classes.buttonText}
                    disabled={!isFormValid || submitLoading}
                  >
                    <ArrowDropDownIcon />
                  </Button>
                </ButtonGroup>
                <Menu anchorEl={showPostOptionsEl} keepMounted open={showPostOptions} onClose={handleToggleCreateMenu}>
                  {scheduledInFuture && <MenuItem onClick={handleToggleScheduler}>Reschedule</MenuItem>}
                  {scheduledInFuture && <MenuItem onClick={handlePostNow}>Post now</MenuItem>}
                  {!scheduledInFuture && <MenuItem onClick={handleToggleScheduler}>Schedule </MenuItem>}
                </Menu>
              </Box>
            </RowBox>

            <DialogDateTimePicker
              open={showScheduler}
              variant="dialog"
              value={visibleFrom || moment().add(1, "days")}
              onChange={handleSchedulerPickedDate}
              onClose={handleToggleScheduler}
              className={classes.visibleFrom}
              TextFieldComponent={() => null}
              showTodayButton
            />
          </Grid>
        </Grid>
      </Hidden>

      <Hidden xsDown>
        <DialogActions className={classes.actions}>
          {scheduledInFuture && (
            <RowBox mr="auto" className={classes.scheduledText}>
              <ScheduleIcon />
              <Box ml={1}>
                Post scheduled for <strong>{getScheduleFormattedDate(visibleFrom)}</strong>
              </Box>
            </RowBox>
          )}

          <Box mr={2}>
            <LinkButton
              onClick={handleClose}
              className={`${classes.button} ${classes.buttonText}`}
              disabled={submitLoading}
            >
              Cancel
            </LinkButton>
          </Box>

          <ButtonGroup
            variant="contained"
            color="primary"
            aria-label={scheduledInFuture ? "Post as scheduled" : "Post now"}
            className={classes.button}
          >
            <Button
              onClick={scheduledInFuture ? handlePostLater : handlePostNow}
              className={classes.buttonText}
              disabled={!isFormValid || submitLoading}
              data-cy="PostCreator-dialog-post"
            >
              {!submitLoading && <>{scheduledInFuture ? "Post as scheduled" : "Post now"}</>}
              {submitLoading && <LoadingSpinner size="24px" delay={false} />}
            </Button>
            <Button
              size="small"
              onClick={handleToggleCreateMenu}
              className={classes.buttonText}
              disabled={!isFormValid || submitLoading}
            >
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
          <Menu anchorEl={showPostOptionsEl} keepMounted open={showPostOptions} onClose={handleToggleCreateMenu}>
            {scheduledInFuture && <MenuItem onClick={handleToggleScheduler}>Reschedule</MenuItem>}
            {scheduledInFuture && <MenuItem onClick={handlePostNow}>Post now</MenuItem>}
            {!scheduledInFuture && <MenuItem onClick={handleToggleScheduler}>Schedule </MenuItem>}
          </Menu>

          <DialogDateTimePicker
            open={showScheduler}
            variant="dialog"
            value={visibleFrom || moment().add(1, "days")}
            onChange={handleSchedulerPickedDate}
            onClose={handleToggleScheduler}
            className={classes.visibleFrom}
            TextFieldComponent={() => null}
            showTodayButton
          />
        </DialogActions>
      </Hidden>
    </>
  )

  return (
    <CreatorMaster
      id="PostCreator-dialog"
      open={open}
      subject="Post"
      form={form}
      isEdit={isEdit}
      isInline={isInline}
      onClose={handleClose}
    />
  )
}

export default PostCreator
